<template>
  <div class="fan-upload-container">
    <h1>{{ $t('fanUpload.title') }}</h1>
    <p class="intro">{{ $t('fanUpload.intro') }}</p>

    <div v-if="uploadSuccess" class="success-message">
      <h2>{{ $t('fanUpload.thankYou') }}</h2>
      <p>{{ $t('fanUpload.successMessage') }}</p>
      <div class="success-icon">
        <i class="fas fa-check-circle"></i>
      </div>
      
      <!-- Aktions-Buttons nach erfolgreichem Upload -->
      <div class="success-actions">
        <button @click="resetUploadSuccess" class="btn-upload-another">
          <i class="fas fa-camera"></i> {{ $t('fanUpload.uploadAnother') }}
        </button>
        <a href="/" class="btn-home">
          <i class="fas fa-home"></i> {{ $t('fanUpload.backToHome') }}
        </a>
      </div>
    </div>

    <form v-else @submit.prevent="uploadPhotos" class="upload-form">
      <div class="form-group">
        <label for="photo">{{ $t('fanUpload.photoLabel') }} *</label>
        <div class="file-input-container">
          <input 
            type="file" 
            id="photo" 
            ref="fileInput"
            @change="handleFileChange" 
            accept="image/*"
            multiple
            required
          />
          <div class="file-previews" v-if="previewUrls.length > 0">
            <div v-for="(preview, index) in previewUrls" :key="index" class="file-preview">
              <img :src="preview" alt="Vorschau" />
              <button type="button" @click="removeImage(index)" class="remove-button">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="selected-count" v-if="previewUrls.length > 1">
              {{ previewUrls.length }} Bilder ausgewählt
            </div>
          </div>
          <div class="custom-file-button" v-if="previewUrls.length === 0" @click="triggerFileInput">
            <i class="fas fa-camera"></i> {{ $t('fanUpload.selectPhoto') }}
          </div>
          <div class="add-more-photos" v-if="previewUrls.length > 0" @click="triggerFileInput">
            <i class="fas fa-plus"></i> Weitere Bilder hinzufügen
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="caption">{{ $t('fanUpload.captionLabel') }}</label>
        <textarea 
          id="caption" 
          v-model="formData.caption" 
          :placeholder="$t('fanUpload.captionPlaceholder')"
        ></textarea>
      </div>

      <div class="form-group">
        <label for="concertDate">{{ $t('fanUpload.dateLabel') }}</label>
        <input 
          type="date" 
          id="concertDate" 
          v-model="formData.concertDate"
          @change="handleDateChange"
        />
        <div v-if="dateLocationSuggestion" class="suggestion-banner">
          <i class="fas fa-lightbulb"></i>
          <span>Am {{ formatDate(formData.concertDate) }} fand ein Konzert in <strong>{{ dateLocationSuggestion }}</strong> statt.</span>
          <button type="button" @click="applyLocationSuggestion" class="suggestion-apply-btn">
            <i class="fas fa-check"></i> Übernehmen
          </button>
        </div>
      </div>

      <div class="form-group location-group">
        <label for="concertLocation">{{ $t('fanUpload.locationLabel') }}</label>
        <div class="location-input-wrapper">
          <input 
            type="text" 
            id="concertLocation" 
            v-model="formData.concertLocation"
            @input="handleLocationInput"
            :placeholder="$t('fanUpload.locationPlaceholder')"
            autocomplete="off"
          />
          <div v-if="locationSuggestions.length > 0" class="location-suggestions">
            <div
              v-for="(suggestion, idx) in locationSuggestions"
              :key="idx"
              @click="selectLocationSuggestion(suggestion)"
              class="location-suggestion-item"
            >
              {{ suggestion }}
            </div>
          </div>
        </div>
      </div>

      <!-- Rechtliche Zustimmung mit modernem Design -->
      <div class="legal-consent-container">
        <div class="legal-consent-wrapper">
          <div class="custom-checkbox">
            <input 
              type="checkbox" 
              id="legalConsent" 
              v-model="legalConsent"
              required
            />
            <span class="checkmark"></span>
          </div>
          <label for="legalConsent" class="legal-text">{{ $t('fanUpload.legalConsent') }} *</label>
        </div>
      </div>

      <div class="submit-container">
        <button 
          type="submit" 
          class="submit-button" 
          :disabled="isSubmitting || selectedFiles.length === 0 || !legalConsent"
        >
          <span v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i> {{ $t('fanUpload.uploading') }}
          </span>
          <span v-else>
            <i class="fas fa-upload"></i> {{ selectedFiles.length > 1 ? 'Bilder hochladen' : $t('fanUpload.submit') }}
          </span>
        </button>
      </div>

      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <p class="required-note">* {{ $t('fanUpload.required') }}</p>
      <p class="policy-note">{{ $t('fanUpload.privacyNote') }}</p>
    </form>
  </div>
</template>

<script>
export default {
  name: 'FanPhotoUpload',
  data() {
    return {
      formData: {
        caption: '',
        concertDate: '',
        concertLocation: ''
      },
      selectedFiles: [],
      previewUrls: [],
      isSubmitting: false,
      uploadSuccess: false,
      errorMessage: '',
      legalConsent: false,
      locationSuggestions: [],
      dateLocationSuggestion: null,
      concertLocations: [],
      dateLocationMap: {},
      debounceTimer: null
    }
  },
  created() {
    // Beim Laden der Komponente frühere Konzertdaten abrufen
    this.fetchPreviousConcertData();
  },
  methods: {
    async fetchPreviousConcertData() {
      try {
        // API-Aufruf für gespeicherte Konzertdaten und -orte
        const response = await fetch('/api/concert_locations.php');
        const data = await response.json();
        
        if (data.success) {
          // Eindeutige Liste von Konzertorten
          this.concertLocations = data.locations || [];
          
          // Zuordnung von Datum zu häufigsten Orten
          this.dateLocationMap = data.dateLocationMap || {};
        }
      } catch (error) {
        console.error('Fehler beim Abrufen der Konzertdaten:', error);
        
        // Fallback-Daten für Demozwecke
        this.concertLocations = [
          'Kulturhaus Osterfeld, Pforzheim',
          'Substage, Karlsruhe',
          'Die Halle, Heidelberg',
          'Zeltival, Karlsruhe',
          'Irish Pub O\'Reillys, Karlsruhe',
          'Scruffy\'s Irish Pub, Karlsruhe',
          'Festhalle Durlach',
          'Café NUN, Karlsruhe'
        ];
        
        this.dateLocationMap = {
          '2023-03-17': 'Irish Pub O\'Reillys, Karlsruhe',
          '2023-06-24': 'Zeltival, Karlsruhe',
          '2023-08-12': 'Kulturhaus Osterfeld, Pforzheim',
          '2023-10-05': 'Die Halle, Heidelberg',
          '2023-12-15': 'Substage, Karlsruhe'
        };
      }
    },
    
    handleFileChange(event) {
      const files = event.target.files
      if (!files || files.length === 0) return
      
      // Bestehende Dateien mit neu ausgewählten zusammenführen
      this.processSelectedFiles(files)
    },
    
    processSelectedFiles(files) {
      // Dateien zum Array hinzufügen
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        this.selectedFiles.push(file)
        
        // Bild-Vorschau erstellen
        const reader = new FileReader()
        reader.onload = e => {
          this.previewUrls.push(e.target.result)
        }
        reader.readAsDataURL(file)
      }
    },
    
    removeImage(index) {
      // Entfernen des Bildes aus den Arrays
      this.selectedFiles.splice(index, 1)
      this.previewUrls.splice(index, 1)
      
      // Wenn keine Bilder mehr ausgewählt sind, File-Input zurücksetzen
      if (this.selectedFiles.length === 0 && this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    
    triggerFileInput() {
      // Klick auf das versteckte File-Input auslösen
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    
    handleDateChange() {
      // Wenn ein Datum ausgewählt wurde, prüfen, ob dafür ein bekannter Ort existiert
      if (this.formData.concertDate && this.dateLocationMap[this.formData.concertDate]) {
        this.dateLocationSuggestion = this.dateLocationMap[this.formData.concertDate];
      } else {
        this.dateLocationSuggestion = null;
      }
    },
    
    applyLocationSuggestion() {
      if (this.dateLocationSuggestion) {
        this.formData.concertLocation = this.dateLocationSuggestion;
        this.dateLocationSuggestion = null; // Vorschlag ausblenden nach Anwendung
      }
    },
    
    handleLocationInput() {
      // Debounce-Funktion für die Standort-Eingabe, um zu viele Updates zu vermeiden
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.updateLocationSuggestions();
      }, 300);
    },
    
    updateLocationSuggestions() {
      const input = this.formData.concertLocation.toLowerCase().trim();
      
      if (!input) {
        this.locationSuggestions = [];
        return;
      }
      
      // Standorte filtern, die mit der Eingabe beginnen oder sie enthalten
      this.locationSuggestions = this.concertLocations.filter(location => {
        return location.toLowerCase().includes(input);
      }).slice(0, 5); // Maximal 5 Vorschläge
    },
    
    selectLocationSuggestion(suggestion) {
      this.formData.concertLocation = suggestion;
      this.locationSuggestions = [];
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return date.toLocaleDateString('de-DE', options);
    },
    
    async uploadPhotos() {
      if (this.selectedFiles.length === 0 || !this.legalConsent) return
      
      this.isSubmitting = true
      this.errorMessage = ''
      let successCount = 0
      
      try {
        // Für jedes Bild einen Upload durchführen
        const uploadPromises = this.selectedFiles.map(async (file) => {
          const formData = new FormData()
          formData.append('image', file)
          formData.append('caption', this.formData.caption || '')
          formData.append('concertDate', this.formData.concertDate || '')
          formData.append('concertLocation', this.formData.concertLocation || '')
          
          const response = await fetch('/api/fan_upload.php', {
            method: 'POST',
            body: formData
          })
          
          const data = await response.json()
          
          if (data.success) {
            successCount++
            return true
          } else {
            console.error('Upload-Fehler:', data.error)
            return false
          }
        })
        
        const results = await Promise.all(uploadPromises)
        
        if (successCount === this.selectedFiles.length) {
          // Alle Uploads erfolgreich
          this.uploadSuccess = true
          this.resetForm()
        } else if (successCount > 0) {
          // Teilweise erfolgreich
          this.errorMessage = `${successCount} von ${this.selectedFiles.length} Bildern wurden erfolgreich hochgeladen.`
          // Wir behalten die nicht hochgeladenen Bilder bei
          this.removeSuccessfulUploads(results)
        } else {
          // Keine erfolgreichen Uploads
          this.errorMessage = this.$t('fanUpload.genericError')
        }
      } catch (error) {
        console.error('Fehler beim Upload:', error)
        this.errorMessage = this.$t('fanUpload.networkError')
      } finally {
        this.isSubmitting = false
      }
    },
    
    removeSuccessfulUploads(results) {
      // Entfernen der erfolgreich hochgeladenen Bilder
      for (let i = results.length - 1; i >= 0; i--) {
        if (results[i]) {
          this.selectedFiles.splice(i, 1)
          this.previewUrls.splice(i, 1)
        }
      }
    },
    
    resetForm() {
      this.formData = {
        caption: '',
        concertDate: '',
        concertLocation: ''
      }
      this.selectedFiles = []
      this.previewUrls = []
      this.legalConsent = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.dateLocationSuggestion = null
      this.locationSuggestions = []
    },
    
    resetUploadSuccess() {
      this.uploadSuccess = false
    }
  }
}
</script>

<style scoped>
.fan-upload-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Open Sans', sans-serif;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #2c3e50;
}

.intro {
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.6;
}

.upload-form {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 20px;
  position: relative;
}

.location-group {
  position: relative;
}

.location-input-wrapper {
  position: relative;
}

.location-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.location-suggestion-item {
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.location-suggestion-item:hover {
  background-color: #f0f0f0;
}

.suggestion-banner {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #4a86e8;
  font-size: 14px;
}

.suggestion-banner i {
  color: #4a86e8;
  margin-right: 8px;
  font-size: 16px;
}

.suggestion-apply-btn {
  margin-left: auto;
  background-color: #4a86e8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.suggestion-apply-btn:hover {
  background-color: #3b78e0;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
}

input[type="file"] {
  display: none;
}

.custom-file-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e7e7e7;
  color: #555;
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  border: 2px dashed #bbb;
  height: 100px;
}

.custom-file-button:hover {
  background-color: #d7d7d7;
}

/* Stil für die Vorschau mehrerer Bilder */
.file-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.file-preview {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  aspect-ratio: 1 / 1;
}

.file-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.remove-button {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  z-index: 2;
}

.selected-count {
  grid-column: 1 / -1;
  text-align: center;
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  margin-top: 5px;
  font-weight: 500;
}

.add-more-photos {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e7e7e7;
  color: #555;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 10px;
  font-size: 14px;
}

.add-more-photos:hover {
  background-color: #d7d7d7;
}

input[type="text"],
input[type="date"],
textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: inherit;
  font-size: 16px;
}

textarea {
  min-height: 100px;
  resize: vertical;
}

/* Styles für die rechtliche Zustimmung */
.legal-consent-container {
  margin: 30px 0;
  padding: 15px;
  background-color: #f0f8ff;
  border-radius: 8px;
  border-left: 4px solid #4caf50;
}

.legal-consent-wrapper {
  display: flex;
  align-items: flex-start;
}

.custom-checkbox {
  position: relative;
  min-width: 24px;
  height: 24px;
  margin-right: 12px;
  margin-top: 2px;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 24px;
  width: 24px;
  background-color: #fff;
  border: 2px solid #ccc;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.custom-checkbox:hover input ~ .checkmark {
  border-color: #aaa;
}

.custom-checkbox input:checked ~ .checkmark {
  background-color: #4caf50;
  border-color: #4caf50;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.custom-checkbox input:checked ~ .checkmark:after {
  display: block;
  left: 8px;
  top: 4px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.legal-text {
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  font-weight: normal;
}

.submit-container {
  text-align: center;
  margin-top: 30px;
}

.submit-button {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover:not(:disabled) {
  background-color: #3e8e41;
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.required-note {
  font-size: 14px;
  margin-top: 20px;
  color: #666;
}

.policy-note {
  font-size: 14px;
  color: #666;
  margin-top: 10px;
  text-align: center;
}

.success-message {
  background-color: #e8f5e9;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.success-icon {
  font-size: 60px;
  color: #4caf50;
  margin: 20px 0;
}

/* Styles für die Erfolgsmeldung und Aktions-Buttons */
.success-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
}

.btn-upload-another, 
.btn-home {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-upload-another {
  background-color: #4caf50;
  color: white;
  border: none;
}

.btn-upload-another:hover {
  background-color: #3e8e41;
}

.btn-home {
  background-color: #f5f5f5;
  color: #555;
  border: 1px solid #ddd;
  text-decoration: none;
}

.btn-home:hover {
  background-color: #ebebeb;
}

.error-message {
  color: #f44336;
  margin-top: 10px;
  text-align: center;
}

@media (max-width: 600px) {
  .fan-upload-container {
    padding: 15px;
  }
  
  .upload-form {
    padding: 15px;
  }
  
  input[type="text"],
  input[type="date"],
  textarea,
  .submit-button {
    font-size: 14px;
  }

  .file-previews {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }

  .legal-consent-container {
    padding: 12px;
    margin: 25px 0;
  }
  
  .legal-text {
    font-size: 13px;
  }
  
  .custom-checkbox {
    min-width: 20px;
    height: 20px;
    margin-right: 10px;
  }
  
  .checkmark {
    height: 20px;
    width: 20px;
  }
  
  .custom-checkbox input:checked ~ .checkmark:after {
    left: 7px;
    top: 3px;
    width: 5px;
    height: 10px;
  }
  
  .success-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .btn-upload-another, 
  .btn-home {
    width: 100%;
  }
  
  .suggestion-banner {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .suggestion-apply-btn {
    margin-left: 0;
    align-self: stretch;
    justify-content: center;
  }
}
</style> 